import { ErrorData, LevelDetails, LoadStats, Events } from 'hls.js';
import { VideogularComponent } from '../../player/videogular/Videogular.Component';

import { HlsJsStreamControllerState } from '../HlsJsStreamControllerState';
import { ILevelDetailsExtended } from '../IHlsJsExtended';
import { IRevConnectInfo } from '@vbrick/vbrick-rev-connect/src/IRevConnectInfo';

import { ContentCue } from './ContentCue';
import { DualHlsPlayer } from './DualHlsPlayer';

export class DualHlsContentPlayer extends DualHlsPlayer {
	public isActive: boolean;

	private isLevelLoaded: boolean;
	private isPlayWhenLevelLoaded: boolean;
	private peggedLevelIndex: number;
	private errorRetryCount: number;
	private streamLoadingStopped: boolean;

	constructor(
		API: VideogularComponent,
		public cue: ContentCue,
		contentPlayerContainer: HTMLElement,
		isLive: boolean,
		isMulticast: boolean,
		pLoader: any,
		fLoader: any,
		startLevel: number,
		revConnectInfo?: IRevConnectInfo
	) {
		super(cue.contentPlaylist.url, buildVideoElement(contentPlayerContainer), {
			activeSource: API.activeSource,
			autoPlay: false,
			config: API.config,
			c2paConfig: API.c2paConfig,
			currentSourceC2paEnabled: API.currentSourceC2paEnabled,
			onVideoError: errorDetails => {
				console.error(`Content player video error: ${errorDetails}`);

				if (errorDetails === this.Hls.ErrorDetails.MANIFEST_PARSING_ERROR) {
					//playlist may not be ready, so attempt to recover
					window.setTimeout(() => this.recover(), 1000);
				}
			}
		} as VideogularComponent, isLive, isMulticast, revConnectInfo, {
			pLoader,
			fLoader,
			debug: !!(document as any).enableContentPlayerDebugHlsJs, //here for quick toggling
			playlistLoadPolicy: {
				default: {
					maxTimeToFirstByteMs: 10000,
					maxLoadTimeMs: 20000,
					timeoutRetry: {
						maxNumRetry: isMulticast ? 11 : 10,
						retryDelayMs: 0,
						maxRetryDelayMs: 0
					},
					errorRetry: {
						maxNumRetry: isMulticast ? 11 : 10,
						retryDelayMs: 1200,
						maxRetryDelayMs: 10000
					}
				}
			}
		}, true);

		this.peggedLevelIndex = startLevel;

		this.forceMetadataPreloading = !isLive;
		this.isActive = false;
		this.isPrimaryPlayer = false;
		this.errorRetryCount = 0;
	}

	public get isReadyToSync(): boolean {
		return !!this.videoElement.duration &&
			!!this.videoElement.seekable.length;
	}

	protected initHlsJsPlayer(): Promise<void> {
		return super.initHlsJsPlayer()
			.then(() => {
				this.player.on(this.Hls.Events.ERROR, (_event, data: ErrorData) => {
					const ErrorDetails = this.Hls.ErrorDetails;

					if (!data.fatal) {
						switch (data.details) {
							case ErrorDetails.FRAG_LOAD_ERROR:
							case ErrorDetails.FRAG_LOAD_TIMEOUT:
							case ErrorDetails.KEY_LOAD_ERROR:
							case ErrorDetails.KEY_LOAD_TIMEOUT:
							case ErrorDetails.LEVEL_LOAD_ERROR:
							case ErrorDetails.LEVEL_LOAD_TIMEOUT:
							case ErrorDetails.REMUX_ALLOC_ERROR:
								this.forceRecoverLevel();
								break;
						}
					}
				});
			});
	}

	public destroy(): void {
		console.info(`Remove content player ${this.cue.contentPlaylist.start} @ ${this.currentTime}`);
		this.videoElement.parentNode?.removeChild(this.videoElement);

		// don't destroy the shared fLoader
		this.hlsJsConfigOverrides.fLoader = null;

		super.destroy();
	}

	public hide(): void {
		this.videoElement.style.opacity = '0';
		this.isPlayWhenLevelLoaded = false;
		this.pause();
	}

	public pause(): void {
		try {
			this.videoElement.pause();
		} catch (ex) {
			//eat the error since it's not of any concern
		}
	}

	public setLevel(levelIndex: number): void {
		if (isNaN(levelIndex) || levelIndex < 0) {
			return;
		}

		this.peggedLevelIndex = levelIndex;

		if (!this.player) {
			return;
		}

		this.player.loadLevel = levelIndex;
	}

	public surface(startPlayback: boolean): void {
		this.isActive = true;

		console.info(`Initiate content playback for ${this.cue.contentPlaylist.start}`);

		this._onPlayerReadyToSurface(startPlayback);
	}

	/**
	 * AV-34418 - when there is endtime indicated in speaker playlist, we need to stop gap controller tick and content
	 * playlist level loading. if it is not stopped then player keeps fetching same playlist over and over and gets into
	 * buffering.
	 */
	public stopActiveStreamLoading(): void {
		if (this.player && !this.streamLoadingStopped) {
			console.warn('content player is loading content that is no longer active, so stopping content loading to avoid false buffering');
			this.streamLoadingStopped = true;
			this.player.trigger(this.Hls.Events.BUFFER_EOS, { type: 'video' });
			//Tried to use this.player.stopLoad() but it froze the content player.
			this.levelController.stopLoad();
		}
	}

	protected onBuffering(): void {
		if (this.isActive) {
			super.onBuffering();

			if (this.isPlaying) {
				this.correctStreamControllerStateIfStuck();
			}
		}
	}

	protected get contentPlayerStartSecs(): number | undefined {
		return this.cue.contentPlaylist.start;
	}

	protected onBufferStalledError(): void {
		//deliberately empty
	}

	protected onLevelLoaded(details: LevelDetails, stats: LoadStats): void {
		const initialProgramDateTimeBefore: Date = this._initialProgramDateTime;

		super.onLevelLoaded(details, stats);

		const initialProgramDateTimeAfter: Date = this._initialProgramDateTime;

		this.validateInitialProgramDateTime(initialProgramDateTimeBefore, initialProgramDateTimeAfter, details as ILevelDetailsExtended);
		this.isLevelLoaded = true;
		this.errorRetryCount = 0;

		if (this.isPlayWhenLevelLoaded) {
			this.isPlayWhenLevelLoaded = false;
			this.play();
		}
	}

	protected onLevelsChanged(event?: string, data?: any): void {
		//Hls.js used to check this befor triggering Level Switch but condition is removed now.
		if (data?.level === this.peggedLevelIndex) {
			return;
		}

		this.errorRetryCount = 0;
		super.onLevelsChanged();

		this.setLevel(this.peggedLevelIndex);
	}

	/**
	 * Restores the level switching recovery mechanism for certain errors that is performed by hls.js out of the box, but disabled due to the pegged manual level.
	 */
	private forceRecoverLevel(): void {
		//retrying one less than configured value because
		//if max number is reached then hls.js set it to fatal error
		//So, before that we will change the nextLoadLevel...
		if (this.errorRetryCount < this.player.config.levelLoadingMaxRetry - 1) {
			this.errorRetryCount++;
			return;
		}
		const numLevels: number = this.player.levels.length;
		let nextLevelIndex: number = this.peggedLevelIndex - 1;

		if (numLevels === 1) {
			return;
		}

		if (nextLevelIndex < 0) {
			nextLevelIndex = this.peggedLevelIndex + 1;
		}

		this.player.nextLoadLevel = nextLevelIndex;
	}

	private _onPlayerReadyToSurface(startPlayback: boolean): void {
		this.videoElement.style.opacity = '1';

		if (startPlayback) {
			//is this the place to add startLoad? checkout later.
			if (this.isLive && this.player && !this.isLevelLoaded) {
				this.player.startLoad();
			}
			if (this.videoElement.duration || this.isLevelLoaded) {
				this.play();
			} else {
				this.isPlayWhenLevelLoaded = true;
			}
		}
	}

	private correctStreamControllerStateIfStuck(): void {
		const { state } = this.streamController;

		if (state === HlsJsStreamControllerState.STOPPED || state === HlsJsStreamControllerState.PARSED) {
			this.streamController.state = HlsJsStreamControllerState.IDLE;
		}
	}

	private validateInitialProgramDateTime(before: Date, after: Date, details: ILevelDetailsExtended): void {
		if (before || (this.cue.isInitial && this.isLive)) {
			return;
		}

		const expectedValueFromCue: Date = this.cue.contentPlaylistStartDateTime;
		const actualValue: Date = after;
		const differenceSec: number = this.toFixed((+actualValue - +expectedValueFromCue) / 1000);
		const playlistUrl: string = details.url;

		console.info(`[VALIDATION] Content initial program time is ${differenceSec} secs from the expected value. ${actualValue.toISOString()} - ${expectedValueFromCue.toISOString()} for `, playlistUrl);
	}
}

function buildVideoElement(contentPlayerContainer: HTMLElement): HTMLVideoElement {
	const videoElement: HTMLVideoElement = document.createElement('video');

	videoElement.muted = true;
	videoElement.style.opacity = '0';

	contentPlayerContainer.appendChild(videoElement);

	return videoElement;
}
